import { useState, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import PromptModal from '../common/PromptModal';
import GuidelinesModal from '../common/GuidelinesModal';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "../ui/tooltip";
import {sendGA4Event} from "../../utils/googleAnalyticsUtils";
import { getPrompt } from '../../services/evaluationService';
import { modelDescriptions, modelsWithoutTemperature } from '../../utils/modelsUtils';

import questionCircle from '../../assets/icons/question_circle.svg';
import viewPrompt from '../../assets/icons/view_prompt.svg';

const OptionsPanel = ({
  onEvaluate,
  onModelChange,
  estimates,
  segments,
  models,
  selectedModelId,
  promptText,
  sourceLanguage,
  targetLanguage,
  guidelines,
  onGuidelinesChange
}) => {
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  const [isGuidelinesModalOpen, setIsGuidelinesModalOpen] = useState(false);
  const [showEmptySegmentsTooltip, setShowEmptySegmentsTooltip] = useState(false);
  const [showLangTooltip, setShowLangTooltip] = useState(false);
  const [isLoadingPrompt, setIsLoadingPrompt] = useState(false);
  const [localPromptText, setLocalPromptText] = useState('');
  const [localGuidelines, setLocalGuidelines] = useState(guidelines);

  // const totalTokens = useMemo(() => {
  //   return (estimates?.estimatedTokensIn || 0) + (estimates?.estimatedTokensOut || 0);
  // }, [estimates]);

  const hasNonEmptySegments = useMemo(() => {
    return segments && segments.some(segment => 
      segment.source.trim() !== '' || segment.target.trim() !== ''
    );
  }, [segments]);

  const modelOptions = useMemo(() =>
    models.map((model) => (
      <SelectItem key={model.id} value={model.id}>
        {model.displayName}
      </SelectItem>
    )),
    [models]
  );
  
  const handleEvaluate = () => {
    sendGA4Event('new_evaluation');

    if (!hasNonEmptySegments) {
      setShowEmptySegmentsTooltip(true);
      return;
    }
    if (!sourceLanguage || !targetLanguage) {
      setShowLangTooltip(true);
      return;
    }
    
    onEvaluate(selectedModelId);
  };

  const getSelectedModelDisplayName = () => {
    const selectedModel = models.find(model => model.id === selectedModelId);
    return selectedModel ? selectedModel.displayName : '';
  };

  const getSelectedModelDescription = () => {
    const selectedModel = models.find(model => model.id === selectedModelId);
    if (!selectedModel) return '';
    return modelDescriptions[selectedModel.id] || '';
  };

  const handleViewPrompt = async () => {
    setIsPromptModalOpen(true);
    setIsLoadingPrompt(true);
    try {
      const prompt = await getPrompt(selectedModelId);
      const text = typeof prompt === 'string' ? prompt : prompt?.evaluationPrompt ?? '';
      setLocalPromptText(text);
    } catch (error) {
      setLocalPromptText('Error loading prompt');
    } finally {
      setIsLoadingPrompt(false);
    }
  };
  
  const handleSaveGuidelines = (text) => {
    setLocalGuidelines(text);
    onGuidelinesChange(text);
  };

  const handleGuidelinesClick = () => {
    setIsGuidelinesModalOpen(true);
  };

  useEffect(() => {
    let timeoutId;
    if (showEmptySegmentsTooltip || showLangTooltip) {
      timeoutId = setTimeout(() => {
        setShowEmptySegmentsTooltip(false);
        setShowLangTooltip(false);
      }, 3000);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [showEmptySegmentsTooltip, showLangTooltip]);

  useEffect(() => {
    setLocalGuidelines(guidelines);
  }, [guidelines]);

  return (
    <Card className="w-full max-w-full">
      <CardContent className="space-y-4 md:space-y-6 p-3 md:p-6">
        <div className="space-y-2">
          <label className="text-base font-medium">Select LLM</label>
          <Select 
            value={selectedModelId}
            onValueChange={onModelChange}
            disabled={models.length === 0}
          >
            <SelectTrigger className="w-full">
              <SelectValue>
                {getSelectedModelDisplayName()}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {modelOptions}
            </SelectContent>
          </Select>
          {getSelectedModelDescription() && (
            <p className="text-[10px] leading-[14px] text-[#6B7280] mt-[4px]">
              {getSelectedModelDescription()}
            </p>
          )}
          {models.find(model => model.id === selectedModelId)?.temperature !== undefined && 
           !modelsWithoutTemperature.includes(selectedModelId) && (
            <div className="flex items-center gap-1 text-[#6B7280] !mt-[4px]">
              <p className="text-[10px] leading-[14px]">Temperature: {models.find(model => model.id === selectedModelId)?.temperature}</p>
              <TooltipProvider>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="sm" className="p-0 h-auto">
                      <img src={questionCircle} alt="QuestionMarkCircledIcon" className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-[200px] p-2">
                    <p className="text-[10px] leading-[14px] text-[#020817]">
                      Temperature controls how creative or predictable the AI's responses are.
                    </p>
                    <p className="text-[10px] leading-[14px] text-[#020817] mt-[4px]">                      
                      A low value like 0.01 makes answers very focused and consistent, avoiding randomness.
                      </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}
        </div>
        <div className="space-y-2 !mt-8">
          <label className="text-base font-medium">Prompt</label>
          <div className="w-full relative bg-muted text-xs text-[#6B7280] rounded-md font-mono cursor-default pointer-events-none p-2 md:p-3 !mt-1" 
          style={{ 
            height: '103px', 
            minHeight: '103px', 
            maxHeight: '103px', 
            overflow: 'auto',
            scrollbarWidth: 'none',  /* Firefox */
            msOverflowStyle: 'none',  /* IE and Edge */
          }}>
            <style>
              {`
                div::-webkit-scrollbar {
                  display: none;  /* Chrome, Safari and Opera */
                }
              `}
            </style>
            {`${promptText.slice(0, 140)}...`}
            <img
              src={viewPrompt}
              alt="View prompt"
              onClick={handleViewPrompt} 
              className="absolute bottom-2 right-2 w-5 h-5 p-[0.125rem] rounded bg-[#DEE7F1] hover:bg-[#CAD8E6] cursor-pointer pointer-events-auto transition-colors"
              style={{ minWidth: 20, minHeight: 20, boxShadow: "0px 1px 2px 0px #0000000D"}}
            />
          </div>
        </div>
        <div className="mt-6">
          <p className="text-base font-medium">Guidelines (optional)</p>
          <p className="text-xs text-[#6B7280] !mt-0">Context, Style Guide, Glossary etc.</p>
          <div className="flex w-full relative">
          <style>
          {`.custom-default-scrollbar::-webkit-scrollbar {
                -webkit-appearance: none;
                height: 2px;
                max-height: 28px;
                width: 2px;
                padding-right: 10px;
                padding-left: 10px;
            }
            .custom-default-scrollbar::-webkit-scrollbar-track {
              background-color: rgba(0, 0, 0, .05);
            }
            .custom-default-scrollbar::-webkit-scrollbar-thumb {
                border-radius: 2px;
                background-color: rgba(0, 0, 0, .2);
                padding-right: 10px;
                padding-left: 10px;
          }`}
          </style>
            <textarea
              className="custom-default-scrollbar w-full bg-white border border-[#E2E8F0] box-shadow-[0px_1px_2px_0px_#0000000D] 
              text-xs text-[#020817] rounded-md font-mono p-2 md:p-3 !mt-1 resize-none 
              focus-visible:outline-none focus-visible:ring-0"
              placeholder={`CONTEXT: \nTranslate a corporate merger agreement to Spanish for legal teams and executives in...`}
              value={localGuidelines}
              onChange={(e) => setLocalGuidelines(e.target.value)}
              onBlur={() => onGuidelinesChange(localGuidelines)}
              style={{
                height: '103px',
                minHeight: '103px',
                maxHeight: '103px',
                overflow: 'auto',
              }}
            />
            <button
              onClick={handleGuidelinesClick}
              className="absolute bottom-2 right-2 w-5 h-5 p-[0.125rem] rounded bg-[#DEE7F1] hover:bg-[#CAD8E6] cursor-pointer pointer-events-auto transition-colors flex items-center justify-center"
              style={{ minWidth: 20, minHeight: 20, boxShadow: "0px 1px 2px 0px #0000000D"}}
              type="button"
            >
              <img
                src={viewPrompt}
                alt="View guidelines"
                className="w-full h-full"
              />
            </button>
          </div>
        </div>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between">
            <span className="text-sm text-[#6B7280]">Source Characters:</span>
            <span className="text-sm font-semibold">{estimates?.sourceChars || 0}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-[#6B7280]">Target Characters:</span>
            <span className="text-sm font-semibold">{estimates?.targetChars || 0}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-[#6B7280]">Total Characters:</span>
            <span className="text-sm font-semibold">{(estimates?.sourceChars || 0) + (estimates?.targetChars || 0)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-[#6B7280]">Segments:</span>
            <span className="text-sm font-semibold">{estimates?.totalSegments || 0}</span>
          </div>
          {/* <div>
            <div className="text-2xl font-semibold">{totalTokens || 0}</div>
            <div className="text-sm text-muted-foreground">Tokens</div>
          </div> */}
        </div>

        <div className="relative">
          <Button 
            className="w-full bg-[#020817] hover:bg-[rgb(17,24,39)]/90 text-sm text-white shadow-[0px_1px_3px_0px_#0000001A]"
            onClick={handleEvaluate}
            disabled={!selectedModelId}
          >
            Evaluate Translation Quality
          </Button>
          {showLangTooltip && (
            <div className="absolute left-0 right-0 mt-2 p-3 bg-amber-50 border border-amber-200 rounded-md shadow-sm text-sm text-amber-800">
              <p>Please select both source and target languages.</p>
            </div>
          )}
          {showEmptySegmentsTooltip && (
            <div className="absolute left-0 right-0 mt-2 p-3 bg-amber-50 border border-amber-200 rounded-md shadow-sm text-sm text-amber-800">
              <p>Please enter at least one segment with source or target text.</p>
            </div>
          )}
        </div>

        <PromptModal 
          isOpen={isPromptModalOpen}
          onClose={() => setIsPromptModalOpen(false)}
          prompt={isLoadingPrompt ? 'Loading prompt...' : localPromptText}
        />
        <GuidelinesModal
          isOpen={isGuidelinesModalOpen}
          onClose={() => setIsGuidelinesModalOpen(false)}
          onSave={handleSaveGuidelines}
          initialText={guidelines}
        />
      </CardContent>
    </Card>
  );
};

export default OptionsPanel;
